import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MapPin, Navigation, Search, X, Check, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { mapsApi } from '@grandxl/api-client';
import { useLocationStore } from '../../store/location.store';
import { reverseGeocode } from '../../hooks/useDetectLocation';
async function fetchPredictions(input) {
    if (!input.trim())
        return [];
    try {
        const res = await mapsApi.placesAutocomplete(input);
        const suggestions = res.data.data.suggestions ?? [];
        return suggestions.map((s) => {
            // Split on the first comma into main + secondary — matches Google's
            // native structured_formatting behaviour for autocomplete predictions.
            const [main, ...rest] = s.description.split(',');
            return {
                place_id: s.place_id,
                description: s.description,
                structured_formatting: {
                    main_text: (main ?? s.description).trim(),
                    secondary_text: rest.join(',').trim(),
                },
            };
        });
    }
    catch {
        return [];
    }
}
async function placeDetails(placeId) {
    try {
        const res = await mapsApi.placeDetails(placeId);
        return res.data.data.result?.geometry?.location ?? null;
    }
    catch {
        return null;
    }
}
export function LocationPickerSheet({ isOpen, onClose }) {
    const { city, displayAddress, setLocation } = useLocationStore();
    const { t } = useTranslation('restaurants');
    const [query, setQuery] = useState('');
    const [predictions, setPredictions] = useState([]);
    const [gpsLoading, setGpsLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    // Focus input when sheet opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 350);
        }
        else {
            setQuery('');
            setPredictions([]);
            setSelected(null);
        }
    }, [isOpen]);
    // Debounced Places search
    useEffect(() => {
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        if (!query.trim()) {
            setPredictions([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const results = await fetchPredictions(query);
                setPredictions(results);
            }
            catch {
                setPredictions([]);
            }
            finally {
                setSearchLoading(false);
            }
        }, 350);
    }, [query]);
    async function handleGPS() {
        if (!navigator.geolocation) {
            toast.error(t('location.gpsNotAvailable'));
            return;
        }
        setGpsLoading(true);
        navigator.geolocation.getCurrentPosition(async (pos) => {
            const { latitude, longitude } = pos.coords;
            try {
                const geo = await reverseGeocode(latitude, longitude);
                if (geo) {
                    setLocation({ lat: latitude, lng: longitude }, geo.city, geo.display, 'gps', geo.state);
                    toast.success(t('location.locationSetTo', { city: geo.city }));
                }
                else {
                    setLocation({ lat: latitude, lng: longitude }, 'Your location', 'Location detected', 'gps', 'Benue');
                    toast.success(t('location.locationDetected'));
                }
                onClose();
            }
            catch {
                toast.error(t('location.locationResolveFailed'));
            }
            finally {
                setGpsLoading(false);
            }
        }, () => {
            setGpsLoading(false);
            toast.error(t('location.locationDenied'));
        }, { timeout: 10000, maximumAge: 60000 });
    }
    async function handleSelectPrediction(pred) {
        setSelected(pred.place_id);
        try {
            const coords = await placeDetails(pred.place_id);
            const cityName = pred.structured_formatting.main_text;
            const display = pred.description;
            setLocation(coords ?? { lat: 0, lng: 0 }, cityName, display, 'manual');
            toast.success(t('location.deliveryAreaSetTo', { city: cityName }));
            onClose();
        }
        catch {
            toast.error(t('location.locationDetailsFailed'));
        }
        finally {
            setSelected(null);
        }
    }
    return (_jsx(AnimatePresence, { children: isOpen && (_jsxs(_Fragment, { children: [_jsx(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2 }, onClick: onClose, className: "fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" }), _jsxs(motion.div, { initial: { y: '100%' }, animate: { y: 0 }, exit: { y: '100%' }, transition: { type: 'spring', stiffness: 380, damping: 38 }, className: "fixed bottom-0 left-0 right-0 z-50 max-h-[90vh] rounded-t-3xl bg-white px-4 pt-3 pb-8 max-w-2xl mx-auto flex flex-col", children: [_jsx("div", { className: "mx-auto mb-4 h-1 w-12 rounded-full bg-gray-200 shrink-0" }), _jsxs("div", { className: "flex items-center justify-between mb-4 shrink-0", children: [_jsxs("div", { children: [_jsx("h2", { className: "font-display font-bold text-lg text-gray-900", children: t('location.title') }), _jsx("p", { className: "text-xs text-gray-400 mt-0.5", children: t('location.subtitle') })] }), _jsx("button", { type: "button", onClick: onClose, className: "p-2 rounded-full hover:bg-gray-100 cursor-pointer transition-colors", "aria-label": t('common:close'), children: _jsx(X, { size: 18, className: "text-gray-500" }) })] }), city && (_jsxs(motion.div, { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, className: "flex items-center gap-2 mb-3 px-3 py-2 bg-primary/5 border border-primary/20 rounded-2xl shrink-0", children: [_jsx(MapPin, { size: 14, className: "text-primary shrink-0" }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-[10px] font-medium text-primary/70 leading-none", children: t('location.currentArea') }), _jsx("p", { className: "text-sm font-semibold text-primary truncate", children: displayAddress ?? city })] }), _jsx(Check, { size: 14, className: "text-primary shrink-0" })] })), _jsxs("button", { type: "button", onClick: handleGPS, disabled: gpsLoading, className: "w-full flex items-center gap-3 px-4 py-3.5 mb-4 rounded-2xl border-2 border-dashed border-primary/30 text-primary cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-60 shrink-0", style: { touchAction: 'manipulation' }, children: [_jsx("div", { className: "h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0", children: gpsLoading
                                        ? _jsx(Loader, { size: 17, className: "animate-spin" })
                                        : _jsx(Navigation, { size: 17 }) }), _jsxs("div", { className: "text-left", children: [_jsx("p", { className: "text-sm font-semibold", children: gpsLoading ? t('location.detecting') : t('location.useCurrentLocation') }), _jsx("p", { className: "text-xs text-primary/60", children: t('location.gpsAutoDetect') })] })] }), _jsxs("div", { className: "relative mb-3 shrink-0", children: [_jsx(Search, { size: 15, className: "absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" }), _jsx("input", { ref: inputRef, type: "text", value: query, onChange: (e) => setQuery(e.target.value), placeholder: t('location.searchPlaceholder'), className: "w-full pl-10 pr-4 py-3.5 rounded-2xl border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" }), searchLoading && (_jsx(Loader, { size: 14, className: "absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" }))] }), _jsxs("div", { className: "overflow-y-auto flex-1", children: [_jsx(AnimatePresence, { children: predictions.map((pred, i) => (_jsxs(motion.button, { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0 }, transition: { delay: i * 0.04, duration: 0.15 }, type: "button", onClick: () => void handleSelectPrediction(pred), disabled: selected === pred.place_id, className: "w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-60", style: { touchAction: 'manipulation' }, children: [_jsx("div", { className: "h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0", children: selected === pred.place_id
                                                    ? _jsx(Loader, { size: 15, className: "animate-spin text-primary" })
                                                    : _jsx(MapPin, { size: 15, className: "text-gray-400" }) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-sm font-semibold text-gray-900 truncate", children: pred.structured_formatting.main_text }), _jsx("p", { className: "text-xs text-gray-400 truncate", children: pred.structured_formatting.secondary_text })] })] }, pred.place_id))) }), query.length > 1 && !searchLoading && predictions.length === 0 && (_jsx(motion.p, { initial: { opacity: 0 }, animate: { opacity: 1 }, className: "text-center text-sm text-gray-400 py-8", children: t('location.noResults', { query }) })), !query && predictions.length === 0 && (_jsxs("div", { className: "pt-1", children: [_jsx("p", { className: "text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1", children: t('location.citiesInBenue') }), [
                                            { name: 'Makurdi', lat: 7.7322, lng: 8.5391 },
                                            { name: 'Gboko', lat: 7.3267, lng: 8.9961 },
                                            { name: 'Otukpo', lat: 7.1833, lng: 8.1333 },
                                            { name: 'Katsina-Ala', lat: 7.1667, lng: 9.2833 },
                                            { name: 'Vandekya', lat: 6.9167, lng: 9.0667 },
                                        ].map(({ name, lat, lng }, i) => (_jsxs(motion.button, { initial: { opacity: 0, y: 4 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.05, duration: 0.15 }, type: "button", onClick: () => {
                                                setLocation({ lat, lng }, name, `${name}, Benue`, 'manual');
                                                toast.success(t('location.deliveryAreaSetTo', { city: name }));
                                                onClose();
                                            }, className: "w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left cursor-pointer hover:bg-gray-50 transition-colors", style: { touchAction: 'manipulation' }, children: [_jsx("div", { className: "h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0", children: _jsx(MapPin, { size: 15, className: "text-gray-400" }) }), _jsxs("div", { children: [_jsx("p", { className: "text-sm font-semibold text-gray-900", children: name }), _jsx("p", { className: "text-xs text-gray-400", children: t('location.benueState') })] })] }, name)))] }))] })] })] })) }));
}
//# sourceMappingURL=LocationPickerSheet.js.map